export type BulletVariant = 'problem' | 'symptom' | 'check';

export interface AsaStep {
  step: string;
  title: string;
  items: string[];
}

export interface AsaBulletSectionData {
  id: string;
  eyebrow: string;
  title: string;
  intro?: string[];
  bullets: string[];
  note?: string;
  closing?: string;
  variant: BulletVariant;
  alt?: boolean;
}

export type AsaBlock =
  | ({ type: 'bullets' } & AsaBulletSectionData)
  | { type: 'process'; eyebrow: string; title: string; steps: AsaStep[] };

export const asaHero = {
  tag: 'Luvira ASA',
  title: 'AIが顧客情報を外部に送り、誤った回答をするリスクを把握していますか？',
  subtitle: '顧客情報の入力、外部サービスとの連携、AIに与えた権限。',
  description:
    '利用状況を把握できていないと、情報漏えいや誤回答が起きた際に、原因や影響範囲を判断しにくくなるおそれがあります。Luvira ASAは、合意した範囲でAIの利用状況・情報・権限・外部連携を確認し、想定されるリスクと次に検討すべき対応を整理する診断サービスです。',
  buttonText: 'AI利用リスクを相談する',
  serviceKey: 'Luvira ASA',
};

export const asaBlocks: AsaBlock[] = [
  {
    type: 'bullets',
    id: 'problem',
    eyebrow: 'Problem',
    title: '見えないAI利用は、問題が起きたときの判断を難しくします。',
    intro: [
      '生成AIやAI Agentは業務を支える一方で、社員ごとの利用状況や設定が会社から見えにくいことがあります。',
    ],
    bullets: [
      '顧客情報や社内情報を、どのAIに入力しているか分からない',
      'AIがどの情報や外部サービスにアクセスできるか把握できていない',
      'AIに与えている権限が、業務に必要な範囲か分からない',
      'AIの出力が確認されずに、社外へ使われていないか不安',
      '問題が起きたとき、影響範囲や対応方法を判断できるか不安',
    ],
    closing: '会社が利用状況を把握できていなければ、問題が起きた際の原因や影響を判断しにくくなります。',
    variant: 'problem',
    alt: false,
  },
  {
    type: 'bullets',
    id: 'scope',
    eyebrow: 'Scope',
    title: 'Luvira ASAが確認すること',
    intro: [
      'AIが業務の中で、どの情報や機能に関わっているかを確認します。',
      '診断では、AIそのものだけでなく、業務での使われ方や関連する設定も対象にします。',
    ],
    bullets: [
      'AIに入力される情報',
      'AIが参照できる情報',
      '接続している外部サービス',
      '設定されている権限や操作範囲',
      '業務の中でAIの出力が使われる場面',
      '想定される情報漏えい、誤送信、誤回答、誤操作のリスク',
    ],
    note: '具体的な対象と確認方法は、診断前に相談して決定します。',
    variant: 'check',
    alt: true,
  },
  {
    type: 'bullets',
    id: 'checklist',
    eyebrow: 'Checklist',
    title: '気になる状態はありませんか？',
    bullets: [
      '社員がどのAIを使っているか、会社で把握できていない',
      '顧客情報や社内資料をAIに入力してよいか、判断基準がない',
      'AIがメール、CRM、チャットなどの外部サービスとつながっている',
      'AIの回答を誰が確認するか決まっていない',
      '利用ルールはあるが、実際の設定や使われ方を確認できていない',
      'AI利用について顧客や社内から質問されたとき、どう答えるか不安がある',
    ],
    closing: '当てはまる項目がある場合、まずは利用状況を確認し、どのような対応が必要か判断することが大切です。',
    variant: 'symptom',
    alt: false,
  },
  {
    type: 'bullets',
    id: 'assessment',
    eyebrow: 'Assessment',
    title: 'Luvira ASAの診断',
    intro: [
      '利用実態を、次の判断に使える情報として整理します。',
      'Luvira ASAでは、事前に合意した対象範囲について、AIの利用状況や関連する情報・権限・外部連携を確認します。確認できた事実と、そこから考えられるリスクを整理して報告します。',
    ],
    bullets: [
      '何を対象に確認したか',
      'どの情報やサービスとの関係を確認できたか',
      'どのようなリスクが考えられるか',
      '業務や顧客対応にどのような影響があり得るか',
      'どの点から対応を検討するか',
    ],
    note: 'すべてのリスクの発見や、特定の基準への適合を保証するものではありません。診断結果には、確認できた範囲と前提条件を明記します。',
    variant: 'check',
    alt: true,
  },
  {
    type: 'process',
    eyebrow: 'Process',
    title: '診断の進め方',
    steps: [
      {
        step: 'STEP 1',
        title: '現状と相談内容を確認',
        items: ['AIの利用状況、気になっている点、診断を希望する対象を伺います。'],
      },
      {
        step: 'STEP 2',
        title: '診断範囲と方法を決定',
        items: ['対象環境、確認に必要な情報や権限、実施方法を相談し、開始前に合意します。'],
      },
      {
        step: 'STEP 3',
        title: '合意した範囲を確認',
        items: [
          '利用状況や関連する情報・権限・外部連携を確認します。確認できる内容は、対象環境や提供情報によって異なります。',
        ],
      },
      {
        step: 'STEP 4',
        title: '結果を報告',
        items: ['確認した範囲、把握できた事実、想定されるリスク、次に検討する事項を報告します。'],
      },
    ],
  },
  {
    type: 'bullets',
    id: 'deliverables',
    eyebrow: 'Deliverables',
    title: '診断後に受け取れる内容',
    intro: ['合意した診断範囲に応じて、以下の内容を整理して報告します。'],
    bullets: [
      '確認対象と確認方法',
      'AI利用状況のうち確認できた内容',
      '情報、外部サービス、権限との関係',
      '想定されるリスクと業務上の影響',
      '対応を検討する項目と優先順位',
      '診断上の前提条件や確認できなかった範囲',
    ],
    note: 'レポートは、診断で確認できた内容を共有するためのものです。顧客・取引先への説明や、社内規程・法令への適合を保証するものではありません。',
    variant: 'check',
    alt: true,
  },
  {
    type: 'bullets',
    id: 'policy',
    eyebrow: 'Policy',
    title: 'AIを安全に活用するために',
    intro: [
      'Luvira ASAは、AIの利用を一律に止めるための診断ではありません。',
      '利用状況やリスクを把握し、AIをどう活用していくかを判断するための情報を整理します。',
    ],
    bullets: [
      'AIの利用状況を把握する',
      '確認できた情報や権限の範囲を知る',
      '想定されるリスクを業務と結びつけて考える',
      '次に確認・検討すべきことを決める',
    ],
    note: '診断後に設定変更、ルール整備、システム改修などの実作業が必要な場合は、診断とは分けて内容と範囲を相談します。',
    variant: 'check',
    alt: false,
  },
];

export const asaFaq: { q: string; a: string }[] = [
  {
    q: 'どのようなAIやシステムが診断対象になりますか？',
    a: '対象環境や確認可能な情報・権限によって異なります。事前に状況を伺い、診断対象と方法を相談して決定します。',
  },
  {
    q: 'すべてのリスクを見つけられますか？',
    a: 'すべてのリスクの発見を保証するものではありません。合意した範囲と確認可能な情報に基づき、確認できた内容と前提条件を報告します。',
  },
  {
    q: '診断後の設定変更やルール作成も依頼できますか？',
    a: '必要な作業の内容と範囲を確認したうえで、診断とは分けて相談します。診断に改善実作業が自動的に含まれるものではありません。',
  },
  {
    q: '顧客や取引先に提出できる証明書が発行されますか？',
    a: '診断結果を整理したレポートを作成します。特定の認証・適合・説明責任を満たす証明書としての発行を保証するものではありません。',
  },
];

export const asaCta = {
  title: 'そのAI利用状況、会社として把握できていますか？',
  description:
    'AIが業務でどの情報やサービスに関わっているのか。まずは、現在の利用状況と確認したい範囲を整理するところからご相談ください。',
  buttonText: 'AI利用リスクを相談する',
};